import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { Chart } from 'react-google-charts';
import { Container, Button, Row, Col } from 'react-bootstrap';
import { FaIdCard } from 'react-icons/fa';

import {
  addMonths,
  eachMonthOfInterval,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
} from 'date-fns';
import api from '../../../services/api';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Loading from '../../../components/Loading';
import { IOption } from '../../../components/SelectSync';

import { useToast } from '../../../hooks/toast';
import { User, useAuth } from '../../../hooks/auth';
import { RelatorioHeader } from '../HeaderRelatorio/styles';
import { Colors } from '../../../styles/constants';

import getValidationErrors from '../../../utils/getValidationErrors';
import getResponseErrors from '../../../utils/getResponseErrors';
import {
  formatPrice,
  STATUS_AGENDA,
  TIPO_USUARIO,
} from '../../../utils/funcoes';
import { ContainerTotais } from './styles';
import HeaderSimples from '../components/HeaderSimples';

type IMedico = User;

interface IMedicoQuantidadeAgendamentosDTO {
  total: number;
  AgeStatus: number;
  AgePlano: boolean;
  mes: string;
}

interface IMedicoQuantidadeRetornosDTO {
  total: number;
  mes: string;
}

type IMedicoQuantidadePedidoExamesDTO = IMedicoQuantidadeRetornosDTO & {
  totalRealizado: number;
  valor: number;
};

interface IMedicoValoresAtendimentosDTO {
  total: number;
  totalValor: number;
  totalValorPago: number;
  mes: string;
}

interface IReturnDashboardMedicoDTO {
  agendamentos: IMedicoQuantidadeAgendamentosDTO[];
  retornos: IMedicoQuantidadeRetornosDTO[];
  valoresAtendimentos: IMedicoValoresAtendimentosDTO[];
  pedidosExame: IMedicoQuantidadePedidoExamesDTO[];
}

interface ITotais {
  totalAtendimentos: string;
  totalAtendimentosParticular: string;
  totalAtendimentosCartao: string;
  totalValorPago: string;
  totalRetorno: string;
  percentualRetorno: string;
  ticketMedio: string;
  totalPedidoExame: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  UsuCodigo: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

interface IGenericType {
  [key: string]: any;
}

const DashboardMedico: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();

  const formRefBuscaAcumulado = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [optionsMedico, setOptionsMedico] = useState<IOption[]>();
  const [graficoAgendamentos, setGraficoAgendamentos] = useState<
    IGenericType[]
  >();
  const [graficoValoresAtendimento, setGraficoValoresAtendimento] = useState<
    IGenericType[]
  >();
  const [graficoAtendimentos, setGraficoAtendimentos] = useState<
    IGenericType[]
  >();
  const [graficoValoresExames, setGraficoValoresExames] = useState<
    IGenericType[]
  >();
  const graficoColors = [
    Colors.primary,
    Colors.verde,
    Colors.danger,
    Colors.cinza,
  ];

  const graficoAtendimentosColors = [
    Colors.verde,
    Colors.primary,
    Colors.cinza,
    Colors.success,
  ];

  const [totais, setTotais] = useState<ITotais>({
    totalAtendimentos: '0',
    totalAtendimentosParticular: '0',
    totalAtendimentosCartao: '0',
    totalValorPago: '0',
    totalRetorno: '0',
    percentualRetorno: '0',
    ticketMedio: '0',
    totalPedidoExame: '0',
  });

  function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();

  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  useEffect(() => {
    async function loadMedicos(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IMedico[]>(`/medicos/all`);

        const options = response.data.map((op: IMedico) => ({
          value: op.UsuCodigo.toString(),
          label: op.UsuNome,
        }));

        setOptionsMedico(options);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar médicos',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    loadMedicos();
  }, [addToast]);

  const initialDataAcumulado = useMemo(() => {
    let dataInicio = addMonths(startOfMonth(new Date()), -6);
    let dataFim = new Date();

    if (query.get('data') && query.get('data')?.length === 10) {
      dataInicio = startOfMonth(parseISO(`${query.get('data')} 03:00:00`));
      if (query.get('dataFim') && query.get('dataFim')?.length === 10) {
        dataFim = endOfMonth(parseISO(`${query.get('dataFim')} 03:00:00`));
      } else {
        dataFim = endOfMonth(parseISO(`${query.get('data')} 03:00:00`));
      }
    }

    const periodo = {
      dataInicio: format(dataInicio, 'yyyy-MM'),
      dataFim: format(dataFim, 'yyyy-MM'),
    };

    const UsuCodigo = query.get('usuario') || '';

    return { ...periodo, UsuCodigo };
  }, [query]);

  const handleSubmitBuscaAcumulado = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBuscaAcumulado.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          UsuCodigo: Yup.string().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setGraficoAgendamentos(undefined);
        setGraficoValoresAtendimento(undefined);
        setGraficoAtendimentos(undefined);
        setGraficoValoresExames(undefined);
        setTotais({
          totalAtendimentos: '...',
          totalAtendimentosParticular: '...',
          totalAtendimentosCartao: '...',
          totalValorPago: '...',
          totalRetorno: '...',
          percentualRetorno: '...',
          ticketMedio: '...',
          totalPedidoExame: '...',
        });

        const dataInicioSelecionada = new Date(
          `${dados.dataInicio.toString()}-01 04:00:00`,
        );
        const dataFimSelecionada = endOfMonth(
          new Date(`${dados.dataFim.toString()}-01 04:00:00`),
        );

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
        });

        const response = await api.get<IReturnDashboardMedicoDTO>(
          `/relatorios/medicodashboard?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&UsuCodigo=${dados.UsuCodigo}`,
        );

        const {
          agendamentos,
          retornos,
          valoresAtendimentos,
          pedidosExame,
        } = response.data;

        const meses = eachMonthOfInterval({
          start: dataInicioSelecionada,
          end: dataFimSelecionada,
        }).map(data => format(data, 'MM/yyyy'));

        const totaisAgendamentos: IGenericType[] = [];
        const totaisValores: IGenericType[] = [];
        const totaisAtendimentos: IGenericType[] = [];
        const totaisExames: IGenericType[] = [];

        let totalAtendimentos = 0;
        let totalAtendimentosParticular = 0;
        let totalAtendimentosCartao = 0;
        let totalValorPago = 0;
        let totalRetorno = 0;
        let totalPedidoExame = 0;

        meses.forEach(mes => {
          const mesFiltrado = agendamentos.filter(rel => rel.mes === mes);
          if (mesFiltrado) {
            const quantidadeRetorno =
              retornos.find(rel => rel.mes === mes)?.total || 0;

            const quantidadePedidoexame =
              pedidosExame.find(rel => rel.mes === mes)?.total || 0;
            const quantidadePedidoexameRealizados =
              pedidosExame.find(rel => rel.mes === mes)?.totalRealizado || 0;

            const valorPedidoexameRealizados =
              pedidosExame.find(rel => rel.mes === mes)?.valor || 0;

            const atendidos = mesFiltrado.filter(
              rel =>
                rel.AgeStatus === STATUS_AGENDA.ATENDIDO ||
                rel.AgeStatus === STATUS_AGENDA.AGUARDANDO_ATENDIMENTO,
            );

            const totalAtendidoParticular = atendidos
              .filter(rel => rel.AgePlano === false)
              .reduce((tot, item) => {
                return tot + Number(item.total || 0);
              }, 0);

            const totalAtendidoCartao = atendidos
              .filter(rel => rel.AgePlano === true)
              .reduce((tot, item) => {
                return tot + Number(item.total || 0);
              }, 0);

            const totalAtendido = atendidos.reduce((tot, item) => {
              return tot + Number(item.total || 0);
            }, 0);

            const totalNaoAtendido = mesFiltrado
              .filter(
                rel =>
                  rel.AgeStatus !== STATUS_AGENDA.ATENDIDO &&
                  rel.AgeStatus !== STATUS_AGENDA.AGUARDANDO_ATENDIMENTO,
              )
              .reduce((tot, item) => {
                return tot + Number(item.total || 0);
              }, 0);

            const totalAgendado = mesFiltrado.reduce((tot, item) => {
              return tot + Number(item.total || 0);
            }, 0);

            let percentualCancelado = (totalNaoAtendido / totalAgendado) * 100;
            let percentualAtendido = (totalAtendido / totalAgendado) * 100;
            let percentualRetorno = (quantidadeRetorno / totalAtendido) * 100;

            if (Number.isNaN(percentualCancelado)) percentualCancelado = 0;
            if (Number.isNaN(percentualAtendido)) percentualAtendido = 0;
            if (Number.isNaN(percentualRetorno)) percentualRetorno = 0;

            totalAtendimentos += totalAtendido;
            totalRetorno += quantidadeRetorno;
            totalPedidoExame += quantidadePedidoexame;
            totalAtendimentosParticular += totalAtendidoParticular;
            totalAtendimentosCartao += totalAtendidoCartao;

            const percentualCanceladoFormatado = `${Number.parseFloat(
              percentualCancelado.toString(),
            ).toFixed(1)}%`;
            const percentualAtendidoFormatado = `${Number.parseFloat(
              percentualAtendido.toString(),
            ).toFixed(1)}%`;
            const percentualRetornoFormatado = `${Number.parseFloat(
              percentualRetorno.toString(),
            ).toFixed(1)}%`;

            totaisAgendamentos.push([
              mes,
              totalAgendado,
              totalAtendido,
              percentualAtendidoFormatado,
              totalNaoAtendido,
              percentualCanceladoFormatado,
            ]);

            totaisAtendimentos.push([
              mes,
              totalAtendido,
              totalAtendidoParticular,
              totalAtendidoCartao,
              quantidadeRetorno,
              percentualRetornoFormatado,
              quantidadePedidoexame,
              quantidadePedidoexameRealizados,
            ]);

            totaisExames.push([
              mes,
              quantidadePedidoexame,
              quantidadePedidoexameRealizados,
              valorPedidoexameRealizados / 1000,
            ]);
          }
          if (!mesFiltrado) {
            totaisAgendamentos.push([mes, 0, 0, 0, 0, 0]);

            totaisAtendimentos.push([mes, 0, 0, 0, 0, 0, 0, 0]);
          }

          // DADOS GRAFICO ATENDIMENTOS VALORES

          const mesValoresFiltrado = valoresAtendimentos.find(
            rel => rel.mes === mes,
          );
          if (mesValoresFiltrado) {
            totalValorPago += mesValoresFiltrado.totalValorPago;
            const ticket =
              mesValoresFiltrado.totalValorPago / mesValoresFiltrado.total;
            totaisValores.push([
              mes,
              mesValoresFiltrado.totalValorPago,
              `Ticket ${formatPrice(ticket)}`,
            ]);
          }
          if (!mesValoresFiltrado) {
            totaisValores.push([mes, 0, '0']);
          }
        });

        const ticketMedio = totalValorPago / totalAtendimentos;
        const percentualRetorno = (totalRetorno / totalAtendimentos) * 100;
        setTotais({
          totalAtendimentos: totalAtendimentos.toString(),
          totalAtendimentosParticular: totalAtendimentosParticular.toString(),
          totalAtendimentosCartao: totalAtendimentosCartao.toString(),
          totalValorPago: formatPrice(totalValorPago),
          totalRetorno: totalRetorno.toString(),
          percentualRetorno: `${Number.parseFloat(
            percentualRetorno.toString(),
          ).toFixed(1)}%`,
          ticketMedio: formatPrice(ticketMedio),
          totalPedidoExame: totalPedidoExame.toString(),
        });

        const graficoAgendamentosLegenda: IGenericType[] = [
          [
            'Mes',
            'Agendado',
            'Atendido',
            { role: 'annotation' },
            'Não Atendido',
            { role: 'annotation' },
          ],
        ];

        setGraficoAgendamentos(
          graficoAgendamentosLegenda.concat(totaisAgendamentos),
        );

        const graficoValoresLegenda: IGenericType[] = [
          ['Mes', 'Valor', { role: 'annotation' }],
        ];

        setGraficoValoresAtendimento(
          graficoValoresLegenda.concat(totaisValores),
        );

        const graficoAtendimentosLegenda: IGenericType[] = [
          [
            'Mes',
            'Atendido',
            'Particular',
            'Cartão',
            'Atendimento X Retorno',
            { role: 'annotation' },
            'Pedidos Exame',
            'Exames Realizados',
          ],
        ];

        setGraficoAtendimentos(
          graficoAtendimentosLegenda.concat(totaisAtendimentos),
        );

        const graficoValoresExamesLegenda: IGenericType[] = [
          ['Mês', 'Total Pedido', 'Total Realizado', 'Valor por mil'],
        ];

        setGraficoValoresExames(
          graficoValoresExamesLegenda.concat(totaisExames),
        );

        // console.log(graficoValoresExamesLegenda.concat(totaisExames));
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBuscaAcumulado.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    if (optionsMedico && optionsMedico.length > 0)
      formRefBuscaAcumulado.current?.submitForm();
  }, [optionsMedico]);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="bg-white border p-2 my-3 d-flex justify-content-between align-items-center">
          <HeaderSimples />
          <h2 id="topoBusca">Dashboard Médico</h2>

          <span className="pr-2">Impresso em {dataExibe?.dataHora}</span>
        </div>

        <div className="bg-white border p-2 mb-2">
          <RelatorioHeader className="mt-2">
            Evolução Médico {dataExibe?.dataDiaSemana}
          </RelatorioHeader>
          <div className="bg-white border p-2">
            <div className="d-print-none">
              {optionsMedico && optionsMedico.length > 0 && user && (
                <Form
                  ref={formRefBuscaAcumulado}
                  initialData={initialDataAcumulado}
                  onSubmit={handleSubmitBuscaAcumulado}
                >
                  <Row>
                    <Col md={3}>
                      <Input
                        name="dataInicio"
                        label="De"
                        type="month"
                        required
                      />
                    </Col>
                    <Col md={3}>
                      <Input name="dataFim" label="Até" type="month" required />
                    </Col>
                    <Col md={4}>
                      <Select
                        name="UsuCodigo"
                        label="Medico"
                        options={optionsMedico}
                      />
                    </Col>
                    <Col md={2}>
                      <br />
                      <Button
                        type="submit"
                        size="sm"
                        variant="outline-secondary"
                      >
                        BUSCAR
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </div>

            <ContainerTotais>
              <div className="text-center bg-light p-3 border rounded">
                <h5>Atendimentos</h5>
                <p style={{ fontSize: '2.5rem' }} className="text-verde mb-1">
                  {totais.totalAtendimentos}
                </p>
                <p style={{ fontSize: '1.4rem' }} className="text-verde mb-0">
                  P {totais.totalAtendimentosParticular} - <FaIdCard />{' '}
                  {totais.totalAtendimentosCartao}
                </p>
              </div>
              <div className="text-center bg-light p-3 border rounded">
                <h5>Retorno</h5>
                <p style={{ fontSize: '2.2rem' }} className="text-danger">
                  {totais.totalRetorno} - {totais.percentualRetorno}
                </p>
              </div>
              <div className="text-center bg-light p-3 border rounded">
                <h5>Valor</h5>
                <p style={{ fontSize: '2rem' }} className="text-primary">
                  {totais.totalValorPago}
                </p>
              </div>
              <div className="text-center bg-light p-3 border rounded">
                <h5>Ticket Médio</h5>
                <p style={{ fontSize: '2.5rem' }} className="text-primary">
                  {totais.ticketMedio}
                </p>
              </div>
              <div className="text-center bg-light p-3 border rounded">
                <h5>Pedidos Exame</h5>
                <p style={{ fontSize: '3rem' }} className="text-verde">
                  {totais.totalPedidoExame}
                </p>
              </div>
            </ContainerTotais>

            <div>
              {graficoAgendamentos && (
                <Chart
                  chartType="ColumnChart"
                  data={graficoAgendamentos}
                  options={{
                    title: `Total Agendamentos`,
                    is3D: true,
                    legend: { position: 'bottom' },
                    colors: graficoColors,
                  }}
                  width="100%"
                  height="300px"
                  legendToggle
                />
              )}
            </div>

            <div>
              {graficoAtendimentos && (
                <Chart
                  chartType="ColumnChart"
                  data={graficoAtendimentos}
                  options={{
                    title: `Atendimentos`,
                    is3D: true,
                    legend: { position: 'bottom' },
                    colors: graficoAtendimentosColors,
                  }}
                  width="100%"
                  height="300px"
                  legendToggle
                />
              )}
            </div>

            <div>
              {graficoValoresAtendimento && (
                <Chart
                  chartType="ColumnChart"
                  data={graficoValoresAtendimento}
                  options={{
                    title: `Valores Atendimentos`,
                    is3D: true,
                    legend: { position: 'bottom' },
                    colors: graficoAtendimentosColors,
                  }}
                  width="100%"
                  height="300px"
                  legendToggle
                />
              )}
            </div>

            <div>
              {graficoValoresExames && (
                <Chart
                  chartType="ComboChart"
                  data={graficoValoresExames}
                  options={{
                    title: `Valores Exames`,
                    is3D: true,
                    legend: { position: 'bottom' },
                    colors: [Colors.verde, Colors.primary, Colors.danger],
                    seriesType: 'bars',
                    series: { 2: { type: 'line' } },
                  }}
                  width="100%"
                  height="300px"
                  legendToggle
                />
              )}
            </div>
          </div>
        </div>
      </>
    </Container>
  );
};

export default DashboardMedico;
